import { z } from 'zod'
import { api } from '@/lib/api/client'
import type { EsquemaDoAcesso } from '@/lib/auth/acesso'
import type { LoginResponse } from '@/types/auth'

/**
 * Só as cinco rotas de sessão vão com credencial (faixa 25 C1, I7). O cookie de renovação
 * (`__Host-omni_refresh`: HttpOnly, host-only da API, `Path=/`) só precisa viajar ou chegar nelas;
 * credencial no client inteiro o mandaria em TODA chamada.
 */
const COM_CREDENCIAL = { withCredentials: true } as const

/**
 * Teto do login: ele roda dentro do trinco entre abas (`login/page.tsx`) — pendurado, travaria todas as abas.
 * 15 s como a troca de senha: o back também faz hash de senha. Estourou → `ApiError(0, 'NETWORK')`.
 */
export const TIMEOUT_LOGIN_MS = 15_000

export async function login(formData: FormData): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/login', formData, { ...COM_CREDENCIAL, timeout: TIMEOUT_LOGIN_MS })
  return data
}

/** 200 de `POST /auth/refresh` (back `app/routes/auth/refresh/__init__.py::RefreshResponse`). */
export const RespostaDeAcessoSchema = z.object({
  access_token: z.string().min(1),
  token_type: z.string(),
  /** SEGUNDOS até o `exp` (≤ 600). Inteiro: o back faz `exp − agora` com `agora` inteiro. */
  expires_in: z.number().int().nonnegative(),
})

export type RespostaDeAcesso = z.infer<typeof RespostaDeAcessoSchema>

/** Teto da renovação: ela roda dentro do trinco entre abas — pendurada, travaria todas as abas. */
export const TIMEOUT_RENOVACAO_MS = 10_000

/**
 * `POST /auth/refresh` (C3): sem corpo; o cookie vai pela credencial e o `Origin` o navegador manda.
 * Sem `Authorization` — quem garante é o interceptor (`client.ts`). Erros sobem tipados; quem classifica
 * sessão morta × indisponível é `lib/auth/renovacao`.
 */
export async function renovarAcesso(timeoutMs: number = TIMEOUT_RENOVACAO_MS): Promise<RespostaDeAcesso> {
  const { data } = await api.post<unknown>('/auth/refresh', undefined, { ...COM_CREDENCIAL, timeout: timeoutMs })
  return RespostaDeAcessoSchema.parse(data)
}

/**
 * 200 de `POST /auth/change-password` (back `app/routes/auth/change_password/__init__.py::ChangePasswordResponse`).
 * Com o back da faixa 25 vem `access_token` + `expires_in` de uma sessão NOVA e o cookie dela. Opcionais de
 * propósito (D17 da faixa 22): back anterior responde só `{success:true}` — exigir dava erro com a senha JÁ trocada.
 */
export const ChangePasswordResponseSchema = z.object({
  success: z.boolean(),
  access_token: z.string().min(1).optional(),
  token_type: z.string().optional(),
  expires_in: z.number().int().nonnegative().optional(),
})

export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>

/** Teto da troca de senha: ela também roda dentro do trinco entre abas — pendurada, travaria todas as abas. */
export const TIMEOUT_TROCA_SENHA_MS = 15_000

export async function changePassword(password: string, newPassword: string): Promise<ChangePasswordResponse> {
  const { data } = await api.post<unknown>(
    '/auth/change-password',
    { password, new_password: newPassword },
    { ...COM_CREDENCIAL, timeout: TIMEOUT_TROCA_SENHA_MS },
  )
  return ChangePasswordResponseSchema.parse(data)
}

/**
 * Teto do aviso de saída ao servidor (faixa 22 §3, D10). Passado o teto, o axios rejeita sem `response` →
 * `ApiError(0, 'NETWORK')`.
 */
export const TIMEOUT_AVISO_SAIDA_MS = 3000

export interface OpcoesDeSaida {
  /**
   * Access DESTE pedido no lugar do da memória: o renovado dentro da própria saída (`saidaNoServidor`,
   * `renovacao`), que não entra na memória de quem está saindo. O interceptor respeita Authorization explícito.
   * Leva o esquema da emissão (faixa 33): renovado de sessão presa é `DPoP`, e o interceptor põe a prova com `ath`.
   */
  acesso?: { token: string; esquema: EsquemaDoAcesso }
  /** Teto deste pedido. Default `TIMEOUT_AVISO_SAIDA_MS`; a saída passa o que sobrou dos 3 s (I14). */
  timeoutMs?: number
}

function configDaSaida({ acesso, timeoutMs = TIMEOUT_AVISO_SAIDA_MS }: OpcoesDeSaida) {
  return {
    ...COM_CREDENCIAL,
    timeout: timeoutMs,
    ...(acesso ? { headers: { Authorization: `${acesso.esquema} ${acesso.token}` } } : {}),
  }
}

/** `POST /auth/logout` → 204 (apaga o cookie). Exige access válido: vencido dá 401 e NÃO apaga (C5). Sessão presa exige prova. */
export async function logout(opcoes: OpcoesDeSaida = {}): Promise<void> {
  await api.post('/auth/logout', undefined, configDaSaida(opcoes))
}

/** `POST /auth/logout-all` → 204. Avança o marco: toda sessão do user morre, inclusive a desta aba. Mesmo 401 do logout. */
export async function logoutAll(opcoes: OpcoesDeSaida = {}): Promise<void> {
  await api.post('/auth/logout-all', undefined, configDaSaida(opcoes))
}
